import {
  CategoriesData,
  Commodities,
  CommoditiesUnit,
  CommodityByCountry,
  SearchResponse,
} from "interfaces/dataInterfaces"
import { blobToDownload } from "utils/helpers"
import { coreApi } from "./coreApi"

type CommodityPayload = Partial<{
  commodityID: number
  commodityId: number
  commodityName: string
  commodityCategoryID: number
  commodityCategoryId: number
}>

type CategoryPayload = {
  commodityCategoryID: number
  commodityCategoryId: number
  commodityCategoryName: string
  commodityCategoryTypeID: number
  commodityCategoryTypeId: number
}

const mapCommodityPayload = (payload: CommodityPayload) => ({
  commodityId: payload.commodityId ?? payload.commodityID,
  commodityName: payload.commodityName,
  commodityCategoryId: payload.commodityCategoryId ?? payload.commodityCategoryID,
})

const mapCategory = (category: CategoryPayload): CategoriesData => ({
  commodityCategoryID: category.commodityCategoryID ?? category.commodityCategoryId,
  commodityCategoryName: category.commodityCategoryName,
  commodityCategoryTypeID: category.commodityCategoryTypeID ?? category.commodityCategoryTypeId,
})

export const commoditiesApi = coreApi.injectEndpoints({
  endpoints: builder => ({
    getCommodities: builder.query<SearchResponse<Commodities>, { skip: number; take: number; query?: string }>({
      query: ({ skip, take, query }) => ({
        url: "Commodities/GetBySearch",
        method: "GET",
        params: { search: query || undefined, skip, take },
      }),
      providesTags: [{ type: "Commodities" as const, id: "LIST" }],
    }),

    addCommodities: builder.mutation<void, CommodityPayload>({
      query: body => ({
        url: "Commodities/Post",
        method: "POST",
        body: mapCommodityPayload(body),
      }),
      invalidatesTags: [{ type: "Commodities", id: "LIST" }],
    }),

    updateCommodities: builder.mutation<void, CommodityPayload>({
      query: body => ({
        url: "Commodities/Put",
        method: "PUT",
        body: mapCommodityPayload(body),
      }),
      invalidatesTags: [{ type: "Commodities", id: "LIST" }],
    }),

    deleteCommodities: builder.mutation<void, number>({
      query: id => ({
        url: `Commodities/Delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Commodities", id: "LIST" }],
    }),

    getCategories: builder.query<SearchResponse<CategoriesData>, { skip?: number; take?: number; query?: string }>({
      query: ({ skip, take, query }) => ({
        url: "CommodityCategories/GetBySearch",
        method: "GET",
        params: { search: query || undefined, skip, take },
      }),
      transformResponse: (response: SearchResponse<CategoryPayload>): SearchResponse<CategoriesData> => ({
        ...response,
        items: response.items?.map(mapCategory) ?? [],
      }),
      providesTags: [{ type: "CommodityCategories" as const, id: "LIST" }],
    }),

    getCommoditiesUnit: builder.query<SearchResponse<CommoditiesUnit>, { skip: number; take: number; query?: string }>({
      query: ({ skip, take, query }) => ({
        url: "CommodityUnits/Get",
        method: "GET",
        params: { search: query || undefined, skip, take },
      }),
      providesTags: [{ type: "CommodityUnits" as const, id: "LIST" }],
    }),

    addCommoditiesUnit: builder.mutation<void, Partial<CommoditiesUnit>>({
      query: body => ({
        url: "CommodityUnits/Post",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "CommodityUnits", id: "LIST" }],
    }),

    updateCommoditiesUnit: builder.mutation<void, Partial<CommoditiesUnit>>({
      query: body => ({
        url: "CommodityUnits/Put",
        method: "PUT",
        body,
      }),
      invalidatesTags: [{ type: "CommodityUnits", id: "LIST" }],
    }),

    deleteCommoditiesUnit: builder.mutation<void, number>({
      query: id => ({
        url: "CommodityUnits/Delete",
        method: "DELETE",
        params: { id },
      }),
      invalidatesTags: [{ type: "CommodityUnits", id: "LIST" }],
    }),

    downloadCommodityList: builder.mutation<void, { adm0Code: number }>({
      query: ({ adm0Code }) => ({
        url: `Commodities/GetCsvByAdm0Code/GetCsvByAdm0Code/${adm0Code}`,
        method: "GET",
        responseHandler: async response => blobToDownload(response, "Prefill_Admin.csv"),
      }),
    }),

    getCommodityByCountry: builder.query<Array<CommodityByCountry>, { admCode: number }>({
      query: ({ admCode }) => {
        const url = `Commodities/GetByAdm0Code/GetByCountry/${admCode}`
        return url
      },
      providesTags: ["Series"],
    }),

    getCommoditiesUnitByCountry: builder.query<CommoditiesUnit[], number>({
      query: adm0code => ({
        url: `CommodityUnits/GetByCountry`,
        method: "GET",
        params: { adm0code },
      }),
    }),
  }),
})

export const {
  useGetCommoditiesQuery,
  useAddCommoditiesMutation,
  useUpdateCommoditiesMutation,
  useDeleteCommoditiesMutation,
  useGetCategoriesQuery,
  useGetCommoditiesUnitQuery,
  useAddCommoditiesUnitMutation,
  useUpdateCommoditiesUnitMutation,
  useDeleteCommoditiesUnitMutation,
  useDownloadCommodityListMutation,
  useGetCommoditiesUnitByCountryQuery,
  useGetCommodityByCountryQuery,
} = commoditiesApi
